import React from 'react';
import styled from 'styled-components';
import StaticField from './StaticField';
import { fieldDisplayName } from '../fillLayout';

interface Props {
  page: Record<string, any>;
  keys: string[];
}

const STORAGE_KEY = 'inspectionNotes.detailsExpanded';

// Read-only fields (isStatic) are auto-filled from the inspection — the
// inspector can't change them, so leaving them interleaved with the editable
// fields just buried the work. Collapsed into one summary here, matching the
// Details tab's "Show details" pattern.
const ReportDetailsCard = ({ page, keys }: Props) => {
  const [expanded, setExpanded] = React.useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? false : stored === 'true';
  });

  if (!keys.length) return null;

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    localStorage.setItem(STORAGE_KEY, String(next));
  };

  return (
    <Card>
      <ToggleButton type="button" onClick={toggle} aria-expanded={expanded}>
        <Chevron $open={expanded}>▾</Chevron>
        <span>Report details</span>
        <Muted>
          {keys.length} auto-filled field{keys.length === 1 ? '' : 's'}
        </Muted>
      </ToggleButton>
      {expanded && (
        <Grid>
          {keys.map(key => (
            <StaticField
              key={key}
              label={fieldDisplayName(key, page[key])}
              value={page[key] && page[key].text}
            />
          ))}
        </Grid>
      )}
    </Card>
  );
};

export default ReportDetailsCard;

const Card = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  padding: 10px 14px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: var(--mainText);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Muted = styled.span`
  margin-left: auto;
  font-weight: 500;
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  color: var(--neutral-500);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px 20px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
`;
